import React, { memo } from "react"
import PropTypes from "prop-types"
import { Button } from "antd"
import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { generateTimeSlots } from "../../utils/calendarHelpers"
import styles from "../../EquipmentCalendar.module.css"
import { Boat } from "ui/Icon/DSIcon/Boat"
import { Text } from "ui/Typography"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import { getDefaultCalendarColor, getPrimaryCalendarTextColor, resolveBookingColors } from "pagesV2/pro/Calendar/calendarColorUtils"
import { getEventColor } from "pagesV2/pro/Calendar/utils"
import { useTheme } from "context/AntdThemeContext"

const renderBookingBlock = (booking, defaultColor, primaryTextColor, onBookingClick) => {
  const previewColors = resolveBookingColors(booking.color, defaultColor, primaryTextColor, getEventColor)
  return (
    <button
      key={booking.id}
      className={styles.bookingBlock}
      style={{
        backgroundColor: previewColors.backgroundColor,
        borderColor: previewColors.borderColor,
        "--duration": booking.duration
      }}
      onClick={() => onBookingClick(booking.uuid)}
      aria-label={`Open booking ${booking.tripName} at ${booking.timeRange}`}
      type="button"
    >
      <Text fw={700} variant="caption" size="md" className={styles.bookingTime} style={{ color: previewColors.textColor || primaryTextColor }}>
        {booking.timeRange}
      </Text>
      <Text className={`${styles.bookingTrip} ${styles.ellipsisText} ellipsis`} style={{ color: previewColors.textColor || primaryTextColor }}>{booking.tripName}</Text>
    </button>
  )
}

/**
 * Day view calendar component
 * Displays a daily calendar grid with time slots and assets
 */
const DayView = ({ assets, dayGridRef, showLeftArrow, showRightArrow, handleScroll, handleHeaderScroll, onBookingClick }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const timeSlots = generateTimeSlots()
  const defaultColor = getDefaultCalendarColor()
  const primaryTextColor = getPrimaryCalendarTextColor(theme)

  // Show empty state if no assets
  if (!assets || assets.length === 0) {
    return (
      <WithEmptyStateWrapper
        name="calendar-assets"
        hasItems={false}
        fallbackTitle={t("equipment.calendar.no_assets", { defaultValue: "No assets found" })}
        fallbackDescription={t("equipment.calendar.no_assets_description", {
          defaultValue: "Try adjusting your filters or add new assets"
        })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <div />
      </WithEmptyStateWrapper>
    )
  }

  return (
    <div className={styles.calendarGrid}>
      {/* Header Row */}
      <div className={styles.gridHeader}>
        {/* Fixed Time Column Header */}
        <div className={styles.timeColumnHeader}>
          <Boat />
        </div>

        {/* Scrollable Asset Headers Container */}
        <div className={styles.assetHeadersContainer}>
          {/* Left Arrow */}
          {showLeftArrow && (
            <Button icon={<LeftOutlined />} className={styles.scrollArrowLeft} onClick={() => handleScroll("left")} shape="circle" />
          )}

          {/* Scrollable Asset Headers */}
          <div className={styles.assetHeadersScroll} ref={dayGridRef} onScroll={handleHeaderScroll}>
            {assets.map((asset) => (
              <div key={asset.id} className={styles.assetColumnHeader}>
                <Text fw={500}>
                  {t("equipment.calendar.asset")} #{asset.number}
                </Text>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <Button icon={<RightOutlined />} className={styles.scrollArrowRight} onClick={() => handleScroll("right")} shape="circle" />
          )}
        </div>
      </div>

      {/* Time Slots Grid */}
      <div className={styles.gridBody}>
        {timeSlots.map((timeSlot, timeIndex) => {
          const renderAssetCell = (asset) => (
            <div key={`${timeSlot}-${asset.id}`} className={styles.timeSlotCell}>
              {asset.bookings
                ?.filter((booking) => booking.startTime === timeIndex)
                .map((booking) => renderBookingBlock(booking, defaultColor, primaryTextColor, onBookingClick))}
            </div>
          )
          return (
            <div key={timeSlot} className={styles.gridRow}>
              <div className={styles.timeLabel}>
                <Text>{timeSlot}</Text>
              </div>
              <div className={styles.assetColumnsContainer}>
                <div className={styles.assetColumnsScroll}>
                  {assets.map(renderAssetCell)}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

DayView.propTypes = {
  assets: PropTypes.array.isRequired,
  dayGridRef: PropTypes.object.isRequired,
  showLeftArrow: PropTypes.bool.isRequired,
  showRightArrow: PropTypes.bool.isRequired,
  handleScroll: PropTypes.func.isRequired,
  handleHeaderScroll: PropTypes.func.isRequired,
  onBookingClick: PropTypes.func.isRequired
}

export default memo(DayView)
